<script lang="ts" setup>
import type { MDCParserResult } from "@nuxtjs/mdc";

type Content = { mdc?: MDCParserResult; txt?: string };

const {
    size = "md",
    modelValue: item = undefined,
    itemClass = undefined,
} = defineProps<{
    size?: "sm" | "md" | "xs";
    modelValue?: string;
    itemClass?: any;
}>();

function isValidUrl(url: string) {
    try {
        new URL(url);
        return true;
    } catch (_) {
        return false;
    }
}

function isImageUrl(url: any) {
    return (
        typeof url === "string" &&
        isValidUrl(url) &&
        url.match(/\.(jpeg|jpg|gif|png)$/i) != null
    );
}

const {
    data: content,
    refresh,
    status,
    error,
} = useAsyncData<Content>(useId(), async () => {
    let value = item ?? "";

    if (!value) {
        return { mdc: undefined, txt: undefined };
    }

    if (typeof value === "string") {
        value = value
            .trim()
            .replaceAll("{", "&#123;")
            .replaceAll("}", "&#125;");
    }

    if (isImageUrl(value)) {
        const img = document.createElement("img");
        img.src = value as string;
        value = img.outerHTML;
    }

    try {
        return { mdc: await parseMarkdown(value.toString()) };
    } catch (e) {
        return { txt: value.toString() };
    }
});

watch(
    () => item,
    () => refresh(),
);

function handleClick(e: Event) {
    if ((e.target as HTMLElement).nodeName === "a") {
        e.preventDefault();
        navigateTo((e.target as HTMLAnchorElement).href, {
            external: true,
        });
    }
}

const defaultClasses =
    "prose prose-img:rounded-xl prose-slate prose-a:text-primary-500 prose-headings:text-primary-500 prose-hr:border-hd-neutral-300 [&_p>br:first-child]:hidden [&_p>br:last-child]:hidden text-pretty";
const classes = computed(() => {
    return [
        itemClass,
        {
            "prose-sm": size === "sm",
            "prose-sm prose-p:!text-xs": size === "xs",
        },
    ];
});

const MDCRenderer = resolveComponent("MDCRenderer");
</script>

<template>
    <NuxtErrorBoundary>
        <template #error>
            {{ content }}
        </template>
        <MDCRenderer
            v-if="content && content.mdc"
            @click="handleClick"
            :body="content.mdc.body"
            :data="content.mdc.data"
            :class="[defaultClasses, ...classes]"
        />
        <div v-else-if="content?.txt" :class="[defaultClasses, ...classes]">
            {{ content.txt }}
        </div>
    </NuxtErrorBoundary>
</template>
